import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** Presentation pieces of the timed settlement page: the step guide and the grouped totals. */

export type SettlementStepNo = 1 | 2 | 3 | 4;

export type SettlementBlocker = 'saveFailed' | 'awardsExceedPool';

/**
 * Where the operator is in the flow: 1 enter members and R/A, 2 count chips in table
 * management, 3 check the preview and confirm, 4 print the sign sheet. A blocker names a
 * problem that keeps the operator from moving on even though the step itself is reached.
 */
export function settlementStep(state: {
  hasDraft: boolean;
  dirty: boolean;
  confirmed: boolean;
  hasPreview: boolean;
  previewStale: boolean;
  missingCounts: number;
  chipDifference: number;
  denominationBlocked: boolean;
  counting: boolean;
  saveFailed: boolean;
  awardsExceedPool: boolean;
}): { step: SettlementStepNo; blocker: SettlementBlocker | null } {
  if (state.saveFailed) return { step: 1, blocker: 'saveFailed' };
  if (state.dirty || (!state.hasDraft && !state.confirmed)) return { step: 1, blocker: null };
  if (state.confirmed) return { step: 4, blocker: null };
  if (
    !state.counting ||
    !state.hasPreview ||
    state.previewStale ||
    state.missingCounts > 0 ||
    state.chipDifference !== 0 ||
    state.denominationBlocked
  )
    return { step: 2, blocker: null };
  if (state.awardsExceedPool) return { step: 3, blocker: 'awardsExceedPool' };
  return { step: 3, blocker: null };
}

const STEP_TARGETS: Record<SettlementStepNo, string> = {
  1: '#settlement-data',
  2: '/tables',
  3: '#settlement-preview',
  4: '#settlement-receipt',
};

const ACTION_BASE =
  'inline-flex min-h-11 items-center justify-center px-4 text-sm font-medium transition-colors';
const PRIMARY_ACTION = `${ACTION_BASE} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE_ACTION = `${ACTION_BASE} border border-base-300 text-base-content hover:bg-base-300`;

/** `emphasis` is false when the current step's own button already is the page's primary action. */
export function SettlementSteps({
  step,
  blocker = null,
  emphasis = true,
}: {
  step: SettlementStepNo;
  blocker?: SettlementBlocker | null;
  emphasis?: boolean;
}) {
  const { t } = useTranslation('tournament');
  const steps: SettlementStepNo[] = [1, 2, 3, 4];
  // A blocker is fixed in the settlement data (rules, members or a failed save).
  const target = blocker ? STEP_TARGETS[1] : STEP_TARGETS[step];
  const ACTION = emphasis ? PRIMARY_ACTION : OUTLINE_ACTION;
  const action = blocker
    ? t('settlementFlow.fixData')
    : step === 2
      ? t('settlementFlow.goToTables')
      : t(`settlementFlow.action${step}`);
  return (
    <section className="border-2 border-primary bg-base-200 print:hidden">
      <ol
        aria-label={t('settlementFlow.label')}
        className="grid grid-cols-2 sm:grid-cols-4 border-b border-base-300"
      >
        {steps.map((n) => (
          <li
            key={n}
            aria-current={n === step ? 'step' : undefined}
            className={`relative flex min-h-11 items-center gap-2 px-3 py-2 text-sm ${
              n === step
                ? 'bg-primary text-primary-content font-bold'
                : n < step
                  ? 'text-base-content'
                  : 'text-base-content/70'
            }`}
          >
            <span className="tabular-nums">{n}</span>
            <span>{t(`settlementFlow.step${n}`)}</span>
            {n < step && <span className="sr-only">{t('settlementFlow.done')}</span>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <p className={`text-base ${blocker ? 'font-semibold text-error' : 'text-base-content'}`}>
          {blocker ? t(`settlementFlow.blocked.${blocker}`) : t(`settlementFlow.hint${step}`)}
        </p>
        {target.startsWith('#') ? (
          <a href={target} className={ACTION}>
            {action}
          </a>
        ) : (
          <Link to={target} className={ACTION}>
            {action}
          </Link>
        )}
      </div>
    </section>
  );
}

export interface SettlementTotals {
  participants: number;
  raCount: number;
  units: number;
  expectedChips: number;
  countedChips: number;
  chipDifference: number;
  prizePoolPrincipal: number;
  activityFee1Total: number;
  netPool: number;
  rankBudget: number;
  rankResidual: number;
  totalAwards: number;
  activityFee2: number;
}

type TotalKey = keyof SettlementTotals;

const GROUPS: Array<{ id: 'entries' | 'chips' | 'money'; keys: TotalKey[] }> = [
  { id: 'entries', keys: ['participants', 'raCount', 'units'] },
  { id: 'chips', keys: ['expectedChips', 'countedChips', 'chipDifference'] },
  {
    id: 'money',
    keys: [
      'prizePoolPrincipal',
      'activityFee1Total',
      'netPool',
      'rankBudget',
      'totalAwards',
      'rankResidual',
      'activityFee2',
    ],
  },
];

// Same formatting as the page's other settlement figures.
const figure = (value: number) => value.toLocaleString('zh-TW');

export function SettlementSummary({
  totals,
  keys,
}: {
  totals: SettlementTotals;
  keys?: TotalKey[];
}) {
  const { t } = useTranslation('tournament');
  const groups = GROUPS.map((group) => ({
    ...group,
    keys: keys ? group.keys.filter((key) => keys.includes(key)) : group.keys,
  })).filter((group) => group.keys.length > 0);
  return (
    <div
      className={`grid gap-3 ${groups.length === 3 ? 'lg:grid-cols-[1fr_1fr_2fr]' : 'lg:grid-cols-[1fr_2fr]'}`}
    >
      {groups.map((group) => (
        <section key={group.id} className="border border-base-300 bg-base-100 p-3">
          <h3 className="mb-2 text-sm font-semibold text-base-content">
            {t(`settlementSummary.${group.id}`)}
          </h3>
          <dl
            className={`grid gap-x-4 gap-y-3 ${group.id === 'money' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-1'}`}
          >
            {group.keys.map((key) => {
              const value = totals[key];
              const tone =
                key === 'chipDifference' && value !== 0 ? 'text-error' : 'text-base-content';
              const size =
                group.id === 'money'
                  ? 'text-xl font-bold underline underline-offset-4 decoration-2'
                  : 'text-lg font-semibold';
              return (
                <div key={key} className="min-w-0">
                  <dt className="text-xs text-base-content/70">{t(`settlementSummary.${key}`)}</dt>
                  <dd className={`${size} ${tone} tabular-nums`}>{figure(value)}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
}
